package org.example.zone360.pages.widgets.matchAnalysisWidget

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.icons.fa.FaAngleRight
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.delay
import org.example.zone360.components.widgetTitleFontSize
import org.example.zone360.componentss.FieldTitleTextNormalSimple
import org.example.zone360.models.MatchAnalysis
import org.example.zone360.models.WidgetDetailResponse
import org.example.zone360.models.WidgetTrend
import org.example.zone360.pages.dashboard.chart.MiniBars
import org.example.zone360.pages.widgets.helper.WidgetTooltipIcon
import org.example.zone360.theme.Palette
import org.example.zone360.theme.toSitePalette
import org.example.zone360.utils.getCurrentMilliseconds
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Canvas
import org.jetbrains.compose.web.dom.CheckboxInput
import org.w3c.dom.HTMLCanvasElement
import org.w3c.dom.HTMLInputElement

@Composable
fun MatchAnalysisNewWidget(
    data: MatchAnalysis,
    widgetNo: Int,
    widgetName: String,
    widgetColor: Color,
    isCoachPage: Boolean = false,
    showSelection: Boolean = false,
    sessionBorder: Boolean = false,
    forceFullWidth: Boolean = false,
    trendData: List<WidgetTrend> = emptyList(),
    isSelected: Boolean = false,
    onSelectionChange: ((Boolean) -> Unit)? = null,
    tooltipText: String = "",
    tooltipTitle: String = "",
    modifier: Modifier = Modifier,
) {
    val scope = rememberCoroutineScope()
    val breakpoint = rememberBreakpoint()
    val sitePalette = ColorMode.current.toSitePalette()
    var isModalOpen by remember { mutableStateOf(false) }
    var widgetDetailResponse by remember { mutableStateOf<WidgetDetailResponse?>(null) }
    var isLoading by remember { mutableStateOf(false) }

    // Unique canvas ID
    val chartId = remember(widgetNo, showSelection) {
        val suffix = getCurrentMilliseconds()
        if (showSelection) "${widgetNo}_matchChart_popup_$suffix"
        else "${widgetNo}_matchChart_$suffix"
    }

// Cleanup on unmount
    DisposableEffect(chartId) {
        onDispose {
            val s = document.createElement("script")
            s.textContent = """
            (function(){
                var c = Chart.getChart('$chartId');
                if(c){ c.destroy(); }
            })();
        """.trimIndent()
            document.body?.appendChild(s)
        }
    }

// Draw chart
    val scoreColor = when {
        data.score.toInt() >= 80 -> "#22C55E"  // Green
        data.score.toInt() >= 60 -> "#22C55E"  // Green
        data.score.toInt() >= 40 -> "#2563EB"  // Blue
        else -> "#EF4444"                      // Red
    }

    LaunchedEffect(data.score) {
        delay(100)
        val canvas = document.getElementById(chartId) as? HTMLCanvasElement
            ?: return@LaunchedEffect

        val remaining = 100 - data.score.toInt()
        val script = """
(function(){
    var existingChart = Chart.getChart('$chartId');
    if(existingChart){ existingChart.destroy(); }

    var canvas = document.getElementById('$chartId');
    if(!canvas) return;
    
    // 🔥 FIX: Set both CSS size AND drawing buffer size to match
    // This prevents blurriness by ensuring the canvas pixel dimensions match display dimensions
    var rect = canvas.getBoundingClientRect();
    var size = Math.round(rect.width || 120);
    
    // Set drawing buffer size (actual pixels)
    canvas.width = size * 2;  // 2x for retina/high DPI displays
    canvas.height = size * 2;
    
    // Set CSS size (display size)
    canvas.style.width = size + 'px';
    canvas.style.height = size + 'px';

    var centerPlugin = {
        id: 'center_$chartId',
        afterDraw: function(chart) {
            var ctx = chart.ctx;
            ctx.save();
            // Scale font for retina display
            var scale = size / 60; // Adjust font size based on actual canvas size
            ctx.font = 'bold ' + (26 * scale) + 'px sans-serif';
            ctx.fillStyle = '$scoreColor';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText('${data.score}', chart.width/2, chart.height/2 - (8 * scale));
            ctx.font = (13 * scale) + 'px sans-serif';
            ctx.fillStyle = '#6B7280';
            ctx.fillText('/100', chart.width/2, chart.height/2 + (16 * scale));
            ctx.restore();
        }
    };

    new Chart('$chartId', {
        type: 'doughnut',
        data: {
            datasets: [{
                data: [${data.score}, $remaining],
                backgroundColor: ['$scoreColor', '#ebeced'],
                borderWidth: 2
             
            }]
        },
        options: {
            plugins: { legend: { display: false }, tooltip: { enabled: false } },
            cutout: '68%',
            responsive: false,
            maintainAspectRatio: false
        },
        plugins: [centerPlugin]
    });
})();
    """.trimIndent()

        val existing = document.getElementById("matchScript_$chartId")
        existing?.remove()
        val scriptEl = document.createElement("script")
        scriptEl.id = "matchScript_$chartId"
        scriptEl.textContent = script
        document.body?.appendChild(scriptEl)
    }


    Box(
        modifier = modifier
            .then(
                when {
                    forceFullWidth -> Modifier.fillMaxWidth()
                    sessionBorder -> Modifier.fillMaxWidth()
                    else -> Modifier.minWidth(
                        when {
                            breakpoint < Breakpoint.MD -> 320.px
                            showSelection -> 280.px
                            window.screen.width in 1200..1500 -> 318.px
                            else -> 350.px
                        }
                    )
                }
            )
            .backgroundColor(sitePalette.modalBackgroundColor.rgb)
            .borderRadius(if (showSelection) 12.px else 16.px)
            .padding(if (showSelection) 12.px else 20.px)
            .border(
                if (showSelection) 2.px else if (sessionBorder) 1.px else 0.px,
                LineStyle.Solid,
                Color.rgb(220, 220, 220)
            )
            .then(
                if (!showSelection) {
                    Modifier.height(380.px)
                } else {
                    Modifier
                }
            )
            .styleModifier {
                property("position", "relative")
            }
    ) {
        // Selection Checkbox
        if (showSelection) {
            Box(
                modifier = Modifier
                    .styleModifier {
                        property("position", "absolute")
                        property("top", "9px")
                        property("right", "9px")
                        property("cursor", "pointer")
                    }
                    .zIndex(9999)
            ) {
                CheckboxInput(checked = isSelected) {
                    onChange { event ->
                        onSelectionChange?.invoke(
                            (event.target as HTMLInputElement).checked
                        )
                    }
                    style {
                        property("transform", "scale(1.4)")
                        property("cursor", "pointer")
                    }
                }
            }
        }

        Column(
            modifier = Modifier
                .fillMaxWidth()
                .height(if (showSelection) 350.px else 340.px)
        ) {
            // Header
            Row(
                verticalAlignment = Alignment.CenterVertically,
                modifier = Modifier.fillMaxWidth().margin(bottom = 12.px)
            ) {
                Box(
                    modifier = Modifier
                        .width(if (sessionBorder) 24.px else 28.px)
                        .height(if (sessionBorder) 24.px else 28.px)
                        .backgroundColor(widgetColor)
                        .borderRadius(6.px)
                        .margin(right = 10.px),
                    contentAlignment = Alignment.Center
                ) {
                    FieldTitleTextNormalSimple(
                        text = widgetNo.toString(),
                        fontSize = 14.px,
                        fontWeight = 700,
                        color = sitePalette.bodyBackgroundSecondary.rgb
                    )
                }
                FieldTitleTextNormalSimple(
                    text = widgetName,
                    fontSize = widgetTitleFontSize(showSelection, sessionBorder, breakpoint),
                    fontWeight = 700
                )
                if (!showSelection) {
                    Box(modifier = Modifier.weight(1f))
                }
                if (tooltipText.isNotEmpty()) {
                    WidgetTooltipIcon(
                        tooltipTitle = tooltipTitle,
                        tooltipText = tooltipText,
                        modifier = Modifier.margin(left = 8.px)
                    )
                }
            }

            // Score and Sub-scores Section
            Column(
                modifier = Modifier.fillMaxWidth().margin(top = 16.px).gap(25.px),
                verticalArrangement = Arrangement.Center,
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                // Circular Score - Using fixed size canvas
                Canvas(
                    Modifier
                        .width(120.px)
                        .height(120.px)
                        .id(chartId)
                        .toAttrs {
                            attr("width", "240")  // 2x for retina display
                            attr("height", "240")
                            attr("style", "width:120px;height:120px;") // Fixed CSS size
                        }
                ) {}
                    // Sub-scores with progress bars
                    Column(
                        modifier = Modifier
                            .padding(left = 10.px),
                        horizontalAlignment = Alignment.CenterHorizontally,
                        verticalArrangement = Arrangement.Center
                    ) {
                        MetricRow(
                            label = "In Possession",
                            value = data.subScores.inPossession.toInt(),
                            color = Color.rgb(34, 197, 94)
                        )
                        MetricRow(
                            label = "Out of Possession",
                            value = data.subScores.outOfPossession.toInt(),
                            color = Color.rgb(239, 68, 68)
                        )
                        MetricRow(
                            label = "Transitions",
                            value = data.subScores.transitions.toInt(),
                            color = Color.rgb(37, 99, 235)
                        )
                }
            }

            Spacer()

            // Footer - View Details
            if (!sessionBorder) {
                if (isCoachPage) {
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .margin(top = if (showSelection) 4.px else 6.px),
                        contentAlignment = Alignment.Center
                    ) {
                        Box(
                            modifier = Modifier
                                .cursor(Cursor.Pointer)
                                .margin(top = 4.px)
                        ) {
                            MiniBars(
                                color = widgetColor,
                                trends = trendData
                            )
                        }
                    }
                } else {
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .height(42.px)
                            .backgroundColor(Palette.ViewDetailBackground.rgbLight)
                            .borderRadius(10.px)
                            .border(
                                1.px,
                                LineStyle.Solid,
                                color = Palette.ViewDetailBorder.rgbLight
                            )
                            .cursor(Cursor.Pointer)
                            .onClick {
                                isModalOpen = true
                            }
                    ) {
                        Box(
                            modifier = Modifier.fillMaxSize(),
                            contentAlignment = Alignment.Center
                        ) {
                            FieldTitleTextNormalSimple(
                                text = "View Details",
                                fontSize = 14.px,
                                fontWeight = 600,
                                color = Palette.ViewDetailText.rgbLight
                            )

                        }
                        Box(
                            modifier = Modifier
                                .fillMaxHeight()
                                .fillMaxWidth()
                                .padding(right = 14.px),
                            contentAlignment = Alignment.CenterEnd
                        ) {
                            FaAngleRight(
                                modifier = Modifier
                                    .width(14.px)
                                    .height(14.px)
                                    .color(Palette.ViewDetailAngle.rgbLight)
                            )
                        }
                    }
                }
            }
        }
    }

    // Modal
    if (isModalOpen) {
        MatchAnalysisDetailsModal(
            widgetNo = widgetNo,
            widgetName = widgetName,
            widgetColor = widgetColor,
            widgetKey = "match_analysis",
            data = null,
            onClose = {
                isModalOpen = false
                widgetDetailResponse = null
            }
        )
    }
}

@Composable
private fun MetricRow(
    label: String,
    value: Int,
    color: Color,
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .margin(bottom = 6.px),
        verticalAlignment = Alignment.CenterVertically
    ) {
        FieldTitleTextNormalSimple(
            text = label,
            width = 120.px,
            fontSize = 13.px,
            color = Color.rgb(55, 65, 81)
        )

//        Box(
//            modifier = Modifier
//                .weight(1f)
//                .height(6.px)
//                .backgroundColor(Color.rgb(229, 231, 235))
//                .borderRadius(100.px)
//        ) {
//            Box(
//                modifier = Modifier
//                    .fillMaxHeight()
//                    .fillMaxWidth((value / 100f).coerceIn(0f, 1f).percent)
//                    .backgroundColor(color)
//                    .borderRadius(100.px)
//            )
//        }

        FieldTitleTextNormalSimple(
            text = value.toString(),
            fontSize = 14.px,
            fontWeight = 700,
            color = color,
            marginLeft = 10.px,
            width = 30.px
        )
    }
}
